/**
 * Tiny typed event bus used by gameplay systems to broadcast facts that
 * the mission system can listen for. The bus is intentionally generic —
 * publishers do NOT know which missions exist; subscribers (like the
 * MissionManager) interpret events.
 *
 * Keep this map narrow. Add new event types only when an actual mission
 * step needs them — no speculative entries.
 */
export interface MissionEventMap {
  /** Player docked into the station interior. */
  readonly stationEntered: { readonly stationId: string };
  /** Player left the station back into space. */
  readonly stationExited: { readonly stationId: string };
  /** An asteroid was destroyed and its loot was credited to inventory.
   *  `count` is the total number of resource items dropped. `source`
   *  identifies the weapon used so missions can filter (e.g. tutorial
   *  step that requires the mining beam specifically). */
  readonly asteroidLootCollected: {
    readonly count: number;
    readonly source: 'gun' | 'beam';
  };
  /** A trade-terminal sale closed successfully. */
  readonly tradeSaleCompleted: {
    readonly itemId: string;
    readonly quantity: number;
    readonly credits: number;
  };
  /** Player paid for a refuel at the engineering counter. `amount` is
   *  the number of fuel units that landed in the tank. */
  readonly shipRefueled: {
    readonly amount: number;
    readonly credits: number;
  };
  /** Player bought a station upgrade (ship module or FPS gear). The
   *  `upgradeId` matches `UpgradeId` in the upgrades module. */
  readonly shopItemPurchased: {
    readonly upgradeId: string;
    readonly price: number;
  };
  /** Player landed on a planet/moon. `planetName` matches
   *  `Landable.displayName`. */
  readonly planetLanded: { readonly planetName: string };
  /** Player entered the cockpit of the surface ship from FPS mode. */
  readonly surfaceShipEntered: {};
  /** Player walked within proximity of the active surface mission
   *  beacon. */
  readonly surfaceMarkerReached: { readonly markerId: string };
  /** A surface ore deposit was destroyed and its yield was credited
   *  to the FPS inventory. */
  readonly surfaceDepositMined: { readonly count: number };
  /** Player saved the game (clicked Save in the pause menu). */
  readonly gameSaved: {};
  /** Player activated a fixed surface terminal (e.g. the Caelis mini
   *  base) by pressing the interact key. */
  readonly surfaceTerminalActivated: { readonly terminalId: string };
  /** Combined sellable inventory value (ship + suit) changed. Emitted
   *  whenever any tracked inventory mutates so missions can gate steps
   *  on "have enough cargo to afford X". The value is potential
   *  credits — sum of `count * sellValue` across sellable items. */
  readonly inventoryValueChanged: { readonly value: number };
  /** Player transitioned from any surface mode (FPS or surface-ship)
   *  back to space-flying. Used by the long-range tutorial to detect
   *  "you're now in space, open the map". */
  readonly returnedToSpace: {};
  /** Player opened the system map overlay (M). */
  readonly systemMapOpened: {};
  /** Player picked a destination on the system map.
   *  `destinationId` matches a `MapBody.id`. Null clears (if ever
   *  emitted). */
  readonly mapDestinationSelected: { readonly destinationId: string };
  /** Ship nose is aligned within the jump facing threshold of the
   *  active destination. Edge-triggered: emits once per becoming
   *  aligned, not continuously. */
  readonly jumpTargetAligned: { readonly destinationId: string };
  /** JumpController entered its charge state (J pressed + alignment
   *  + fuel checks all passed). */
  readonly jumpChargeStarted: { readonly destinationId: string };
  /** Hyperspace jump completed successfully (ship arrived inside the
   *  destination's `arrivalRadius`). */
  readonly hyperspaceJumpCompleted: { readonly destinationId: string };
  /** Player ship's fuel transitioned from > 0 to <= 0. Edge-triggered. */
  readonly fuelDepleted: {};
  /** Player switched to the Radio tab in the inventory overlay. */
  readonly inventoryRadioOpened: {};
  /** Player clicked Request Rescue in the radio panel. */
  readonly rescueRequested: {};
  /** Recovery tug reached the player's ship. Edge-triggered: fires
   *  once per arrival, when the rescue service transitions into the
   *  'arrived' state. */
  readonly rescueTugArrived: {};
  /** Player accepted the rescue tow at the cockpit prompt — completes
   *  the recovery flow. */
  readonly rescueTowAccepted: {};
  /** Player opened a station counter shop (engineering / market /
   *  armory / suit). Used by the long-range tutorial to detect "go
   *  to the refuel point" or "go to the market". */
  readonly stationServiceOpened: {
    readonly service: 'ship-upgrade' | 'trade-terminal' | 'weapon-tool' | 'suit-upgrade';
  };
  /** Player ship's fuel level passed a threshold from below to above.
   *  Edge-triggered so listeners only fire on the crossing event. */
  readonly fuelRefilled: { readonly fuelRatio: number };
  /** Player ship is within proximity of the active mission marker.
   *  `token` matches the active step's `waypointTarget`. Edge-triggered
   *  per token so the manager doesn't repeatedly fire. */
  readonly missionTargetReached: { readonly token: string };
  /** A NPC ship was destroyed. `missionTag` is set when the director
   *  flagged the ship as a tutorial target before it died, so missions
   *  can match the right kill without leaking ids into other systems. */
  readonly missionShipDestroyed: { readonly missionTag: string };
  /** A piece of mission cargo / loot was credited to the player's
   *  inventory. `kind` describes what — distinguishes pirate loot from
   *  cargo theft. */
  readonly missionItemCollected: { readonly kind: 'pirate-loot' | 'cargo' };
  /** Player opened the central station mission terminal (interacted
   *  with the holo at the dais). */
  readonly missionTerminalOpened: {};
  /** Final tutorial mission completed. Once persisted via the save,
   *  the campaign is considered done and the player is free-roaming. */
  readonly tutorialCampaignCompleted: {};
}

export type MissionEventType = keyof MissionEventMap;

export type MissionEventListener<K extends MissionEventType> = (
  payload: MissionEventMap[K]
) => void;

export class MissionEventBus {
  private readonly listeners = new Map<
    MissionEventType,
    Set<MissionEventListener<MissionEventType>>
  >();

  on<K extends MissionEventType>(
    type: K,
    listener: MissionEventListener<K>
  ): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener as MissionEventListener<MissionEventType>);
    return () => {
      set?.delete(listener as MissionEventListener<MissionEventType>);
    };
  }

  emit<K extends MissionEventType>(type: K, payload: MissionEventMap[K]): void {
    const set = this.listeners.get(type);
    if (!set) return;
    for (const listener of set) {
      (listener as MissionEventListener<K>)(payload);
    }
  }
}
